import { LucideExternalLink, LucideVideo } from 'lucide-react'
import { ListingStaticMap } from '~/components/common'
import {
  ListingBrokers,
  ListingDescription,
  ListingFeatured,
  ListingFiles,
  ListingGallery,
  ListingMeta,
  ListingSpaces,
} from '~/components/listing'
import CreateDialog from '~/components/reports/create-dialog'
import { Badge, Button } from '~/components/ui'
import { STATUSES } from '~/constants'
import {
  cn,
  fmtAddress,
  getListingTypeLabel,
  getPropertyTypeClassName,
} from '~/lib'
import type { Client, Listing } from '~/models'
import ParcelDataDialog from './parcel-data-dialog'

interface DetailProps {
  client: Client
  listing: Listing
}

const Detail = ({ client, listing }: DetailProps) => {
  return (
    <main className="px-4 py-8">
      <div className="mx-auto max-w-6xl">
        <>
          <div className="-mb-8 flex justify-end gap-4 sm:mb-4">
            <div className="hidden md:block">
              <CreateDialog singleListing={true} listingIds={[listing.id]}>
                <Button variant="outline" size="sm">
                  Download PDF
                </Button>
              </CreateDialog>
            </div>
            {listing.source_url && listing.source_url.includes('http') && (
              <div className="text-right">
                <Button asChild size="sm">
                  <a
                    href={listing.source_url}
                    rel="noopener noreferrer"
                    target="_blank">
                    View Source
                  </a>
                </Button>
              </div>
            )}
          </div>
        </>

        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="flex items-center justify-between gap-8 sm:items-start">
            <Badge className="text-base hover:bg-primary sm:mt-1">
              {STATUSES[listing.status]}
            </Badge>
          </div>
          <h1 className="flex items-center gap-1 text-3xl font-medium text-foreground">
            {listing.featured && <ListingFeatured className="h-7 w-7" />}
            {listing.name || listing.property.address}
          </h1>
        </div>
        <div className="flex items-center justify-between">
          <p className="mb-4 mt-1 text-xl font-light text-muted-foreground">
            {fmtAddress(listing.property)}
          </p>
        </div>

        <div className="flex flex-col-reverse gap-8 lg:flex-row">
          <div className="space-y-8 lg:w-[62%]">
            <div className="rounded-md border shadow-lg">
              <div className="break-inside-avoid overflow-hidden rounded-md">
                <ListingGallery listing={listing} />
              </div>
            </div>

            <div>
              <h4 className="mb-4 text-xl font-medium">Overview</h4>
              <ListingDescription listing={listing} />
            </div>

            {listing.type != 'sale' &&
              listing.suites.filter((s) => s.available).length > 1 && (
                <div className="break-inside-avoid">
                  <h4 className="mb-4 text-xl font-medium">
                    {listing.status === 'leased'
                      ? 'Leased Spaces'
                      : 'Lease Spaces'}
                  </h4>
                  <ListingSpaces listing={listing} />
                </div>
              )}

            <>
              {(listing.files || []).length > 0 && (
                <div className="max-w-md break-inside-avoid">
                  <h4 className="mb-4 text-xl font-medium">Files</h4>
                  <ListingFiles listing={listing} />
                </div>
              )}
              {listing.meta.virtual_tour_link && (
                <div className="max-w-md break-inside-avoid">
                  <h4 className="mb-4 text-xl font-medium">Links</h4>
                  <div className="sm:col-span-2">
                    <div className="mt-2 text-base text-foreground">
                      <ul
                        role="list"
                        className="divide-y divide-border rounded-md border">
                        <li>
                          <a
                            href={listing.meta.virtual_tour_link}
                            target="_blank"
                            className="flex items-center justify-between py-3 pl-3 pr-4 text-base hover:bg-muted">
                            <div className="flex w-0 flex-1 items-center">
                              <LucideVideo
                                className="h-5 w-5 flex-shrink-0 text-muted-foreground"
                                aria-hidden="true"
                              />

                              <span className="ml-2 w-0 flex-1 truncate">
                                View Virtual Tour
                              </span>
                            </div>
                            <div className="ml-4 flex-shrink-0">
                              <LucideExternalLink className="h-5 w-5 print:hidden" />
                            </div>
                          </a>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </>

            <ListingBrokers
              client={client}
              className="block break-inside-avoid md:hidden"
              listing={listing}
            />
          </div>
          <div className="space-y-8 lg:w-[38%]">
            <div>
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <h2 className="mr-2 text-2xl font-medium">
                  {getListingTypeLabel(listing)}
                </h2>
                {listing.labels &&
                  listing.labels.map((label) => (
                    <Badge
                      key={label}
                      className="bg-primary text-base text-white hover:bg-primary">
                      {label}
                    </Badge>
                  ))}
                {listing.property_types ? (
                  listing.property_types.map((value: any, index: any) => {
                    return (
                      <Badge
                        key={index}
                        className={cn(
                          'text-base',
                          getPropertyTypeClassName(value)
                        )}>
                        <span>{value}</span>
                      </Badge>
                    )
                  })
                ) : (
                  <Badge
                    className={cn(
                      'text-base',
                      getPropertyTypeClassName('Other')
                    )}>
                    <span>Other</span>
                  </Badge>
                )}
              </div>
              {(listing.meta.property_subtypes || []).length > 0 && (
                <p className="mb-2 italic">
                  Secondary Uses: {listing.meta.property_subtypes!.join(', ')}
                </p>
              )}
              <ListingMeta className="text-lg" listing={listing} />
              {client.parcel_data_enabled && (
                <div className="mt-2 text-right">
                  <ParcelDataDialog listing={listing} />
                </div>
              )}
            </div>

            <ListingBrokers
              client={client}
              className="hidden md:block"
              listing={listing}></ListingBrokers>
          </div>
        </div>
      </div>{' '}
      <div className="mx-auto mt-6 hidden max-w-6xl py-2 md:block">
        <h4 className="mb-4 mr-2 text-xl font-medium">Mobility Data</h4>
        {listing.property.zip_code && (
          <iframe
            title="Placer.ai Zip Code Map"
            src={`https://embedded-widget.placer.ai/widgets/v1/zip-code-map/?zipCode=${
              listing.property.zip_code
            }&address=${fmtAddress(listing.property)}`}
            loading="lazy"
            allowFullScreen={true}
            allow="clipboard-write"
            referrerPolicy="origin"
            scrolling="no"
            className="mb-8"
            style={{
              border: '1px solid rgb(229, 229, 229)',
              borderRadius: '8px',
              width: '100%',
              height: '600px',
            }}
          />
        )}
      </div>
      <div className="mx-auto mt-4 h-96 w-full max-w-6xl break-inside-avoid pb-16 md:mt-8">
        <ListingStaticMap listing={listing} />
      </div>
    </main>
  )
}

export default Detail
